/**
 * CDP SDK Plugin Base
 * Base utilities and types for plugin development
 */

// Re-export plugin types from core
export type {
  CDPPlugin,
  CDPCoreAPI,
  CDPCoreConfig,
  EventPayload,
  PageEventPayload,
  TrackEventPayload,
  IdentifyEventPayload,
  GroupEventPayload,
  EventContext,
  SessionData,
  DeviceFingerprint,
  ConsentPreferences,
  ConsentType,
} from '../core/types';

// =============================================================================
// PLUGIN UTILITIES
// =============================================================================

/**
 * Create a typed plugin with default implementations
 */
export function definePlugin<T extends object = object>(options: {
  name: string;
  version: string;
  install: (core: import('../core/types').CDPCoreAPI) => void;
  uninstall?: () => void;
  onInit?: (config: import('../core/types').CDPCoreConfig) => void;
  beforeSend?: (
    payload: import('../core/types').EventPayload
  ) => import('../core/types').EventPayload | null;
  afterSend?: (
    payload: import('../core/types').EventPayload,
    success: boolean
  ) => void;
  onUnload?: () => void;
  onHidden?: () => void;
  onPageChange?: (url: string) => void;
  config?: T;
}): import('../core/types').CDPPlugin & { config: T } {
  return {
    name: options.name,
    version: options.version,
    install: options.install,
    uninstall: options.uninstall,
    onInit: options.onInit,
    beforeSend: options.beforeSend,
    afterSend: options.afterSend,
    onUnload: options.onUnload,
    onHidden: options.onHidden,
    onPageChange: options.onPageChange,
    config: options.config || ({} as T),
  };
}

// =============================================================================
// DOM UTILITIES FOR PLUGINS
// =============================================================================

/**
 * Safely query a single element
 */
export function $(selector: string, context: Element | Document = document): Element | null {
  try {
    return context.querySelector(selector);
  } catch {
    return null;
  }
}

/**
 * Safely query multiple elements
 */
export function $$(selector: string, context: Element | Document = document): Element[] {
  try {
    return Array.from(context.querySelectorAll(selector));
  } catch {
    return [];
  }
}

/**
 * Get text content from an element, trimmed
 */
export function getText(element: Element | null): string | null {
  if (!element) return null;
  const text = element.textContent?.trim();
  return text || null;
}

/**
 * Get attribute value from an element
 */
export function getAttr(element: Element | null, attr: string): string | null {
  if (!element) return null;
  return element.getAttribute(attr);
}

/**
 * Get data attribute value from an element
 */
export function getData(element: Element | null, key: string): string | null {
  if (!element) return null;
  return (element as HTMLElement).dataset?.[key] ?? null;
}

/**
 * Check if element matches selector
 */
export function matches(element: Element | null, selector: string): boolean {
  if (!element) return false;
  try {
    return element.matches(selector);
  } catch {
    return false;
  }
}

/**
 * Find closest ancestor matching selector
 */
export function closest(element: Element | null, selector: string): Element | null {
  if (!element) return null;
  try {
    return element.closest(selector);
  } catch {
    return null;
  }
}

// =============================================================================
// EVENT UTILITIES FOR PLUGINS
// =============================================================================

/**
 * Add event listener with automatic cleanup tracking
 */
export function addListener<K extends keyof WindowEventMap>(
  target: Window,
  type: K,
  listener: (ev: WindowEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): () => void;
export function addListener<K extends keyof DocumentEventMap>(
  target: Document,
  type: K,
  listener: (ev: DocumentEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): () => void;
export function addListener<K extends keyof HTMLElementEventMap>(
  target: HTMLElement,
  type: K,
  listener: (ev: HTMLElementEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): () => void;
export function addListener(
  target: EventTarget,
  type: string,
  listener: EventListener,
  options?: boolean | AddEventListenerOptions
): () => void {
  target.addEventListener(type, listener, options);
  return () => target.removeEventListener(type, listener, options);
}

/**
 * Observe DOM mutations
 */
export function observeDOM(
  target: Node,
  callback: MutationCallback,
  options: MutationObserverInit = { childList: true, subtree: true }
): () => void {
  const observer = new MutationObserver(callback);
  observer.observe(target, options);
  return () => observer.disconnect();
}

/**
 * Observe element intersection (visibility)
 */
export function observeIntersection(
  target: Element,
  callback: IntersectionObserverCallback,
  options?: IntersectionObserverInit
): () => void {
  const observer = new IntersectionObserver(callback, options);
  observer.observe(target);
  return () => observer.disconnect();
}

// =============================================================================
// URL UTILITIES FOR PLUGINS
// =============================================================================

/**
 * Parse URL into components
 */
export function parseUrl(url: string): URL | null {
  try {
    return new URL(url, window.location.origin);
  } catch {
    return null;
  }
}

/**
 * Get query parameter from current URL
 */
export function getQueryParam(key: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

/**
 * Check if current URL matches pattern
 */
export function urlMatches(pattern: string | RegExp): boolean {
  const url = window.location.href;
  if (typeof pattern === 'string') {
    return url.includes(pattern);
  }
  return pattern.test(url);
}

// =============================================================================
// DATA EXTRACTION UTILITIES
// =============================================================================

/**
 * Extract JSON-LD data from page
 */
export function getJsonLd<T = unknown>(type?: string): T | null {
  const scripts = $$('script[type="application/ld+json"]');

  // Publishers wrap `@type` in an array ("@type": ["NewsArticle"]) often enough
  // that a strict equality check misses real articles.
  const matchesType = (item: unknown): boolean => {
    if (!item || typeof item !== 'object') return false;
    const t = (item as { '@type'?: string | string[] })['@type'];
    return Array.isArray(t) ? t.includes(type as string) : t === type;
  };

  for (const script of scripts) {
    try {
      const data = JSON.parse(script.textContent || '');

      if (!type) return data as T;

      // A block can be a single node, a top-level array of nodes (dir.bg
      // serves [NewsArticle, BreadcrumbList]), or a node with an @graph list.
      const nodes: unknown[] = Array.isArray(data)
        ? data
        : Array.isArray(data['@graph'])
          ? data['@graph']
          : [data];
      const found = nodes.find(matchesType);
      if (found) return found as T;
    } catch {
      // Ignore JSON-LD parse errors
    }
  }

  return null;
}

/**
 * Extract Open Graph meta tags
 */
export function getOpenGraph(): Record<string, string> {
  const og: Record<string, string> = {};
  const metas = $$('meta[property^="og:"]');

  for (const meta of metas) {
    const property = getAttr(meta, 'property');
    const content = getAttr(meta, 'content');
    if (property && content) {
      og[property.replace('og:', '')] = content;
    }
  }

  return og;
}

/**
 * Extract meta tag content
 */
export function getMeta(name: string): string | null {
  const meta = $(`meta[name="${name}"], meta[property="${name}"]`);
  return getAttr(meta, 'content');
}

// =============================================================================
// SCROLL UTILITIES FOR PLUGINS
// =============================================================================

/**
 * Get current scroll percentage.
 *
 * document.body is NULL while the SDK boots from <head> (CloudCart injects
 * the snippet early) — the engagement tracker calls this synchronously at
 * init, and the unguarded body.scrollHeight deref threw INSIDE plugin
 * onInit, killing every interaction listener registered after it. That was
 * the entire optibox Product Added collapse (2026-07-01, the day Phase 0a
 * added commerce engagement to the CloudCart plugin; diagnosed live
 * 2026-07-21: page events flowed, clicks dead, console showed the throw).
 */
export function getScrollPercent(): number {
  const body = document.body;
  const doc = document.documentElement;
  if (!body && !doc) return 0;

  const docHeight = Math.max(body?.scrollHeight ?? 0, doc?.scrollHeight ?? 0);
  const winHeight = window.innerHeight;
  const scrollTop = window.scrollY || doc?.scrollTop || 0;

  const scrollableHeight = docHeight - winHeight;
  if (scrollableHeight <= 0) return 100;

  return Math.min(100, Math.round((scrollTop / scrollableHeight) * 100));
}

/**
 * Get element visibility percentage
 */
export function getElementVisibility(element: Element): number {
  const rect = element.getBoundingClientRect();
  const viewHeight = window.innerHeight;

  if (rect.bottom < 0 || rect.top > viewHeight) {
    return 0;
  }

  const visibleTop = Math.max(0, rect.top);
  const visibleBottom = Math.min(viewHeight, rect.bottom);
  const visibleHeight = visibleBottom - visibleTop;

  return Math.round((visibleHeight / rect.height) * 100);
}

// =============================================================================
// TIMING UTILITIES FOR PLUGINS
// =============================================================================

// Re-export from core to avoid duplication
export { debounce, throttle } from '../core/utils';

/**
 * Wait for a condition to be true
 */
export function waitFor(
  condition: () => boolean,
  timeout = 5000,
  interval = 100
): Promise<boolean> {
  return new Promise((resolve) => {
    if (condition()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const check = setInterval(() => {
      if (condition()) {
        clearInterval(check);
        resolve(true);
      } else if (Date.now() - startTime >= timeout) {
        clearInterval(check);
        resolve(false);
      }
    }, interval);
  });
}

/**
 * Wait for an element to appear in DOM
 */
export function waitForElement(
  selector: string,
  timeout = 5000
): Promise<Element | null> {
  return new Promise((resolve) => {
    const element = $(selector);
    if (element) {
      resolve(element);
      return;
    }

    const observer = new MutationObserver(() => {
      const el = $(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve($(selector));
    }, timeout);
  });
}
